// Η προκαταβολή πελάτη στην πληρωμή του Take Away, όπως φαίνεται στο πάνελ της παραγγελίας, στο παράθυρο
// ολοκλήρωσης και στο τιμολόγιο: κουμπί «Προκαταβολή πελάτη» ή η γραμμή «Προκαταβολή −X €» με το
// υπόλοιπο που μένει να πληρωθεί, «Αλλαγή» και «Χωρίς προκαταβολή».

import React from 'react';
import { HandCoins, X } from 'lucide-react';
import { money } from './model';
import { useDT } from './ui';
import type { TakeAwayDepositSelection } from './useTakeAwayDeposit';

export default function TakeAwayDepositLine({ selection, appliedCents, dueCents, onOpen, onRemove, disabled, compact }: {
  selection: TakeAwayDepositSelection | null;
  appliedCents: number;
  dueCents: number;
  onOpen: () => void;
  onRemove: () => void;
  disabled?: boolean;
  /** Χαμηλότερο κουμπί (πάνελ παραγγελίας). */
  compact?: boolean;
}) {
  const dt = useDT();
  if (!selection) {
    return (
      <button type="button" onClick={onOpen} disabled={disabled} data-testid="takeaway-deposit-open"
        className={`flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-line-strong bg-surface-1 px-3 font-semibold text-t2 transition-colors hover:border-blue-500 hover:text-t1 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 ${compact ? 'h-11 text-[14px]' : 'h-12 text-[15px]'}`}>
        <HandCoins className="h-5 w-5" />
        {dt('deposits:takeaway.button')}
      </button>
    );
  }
  const who = selection.customer.name || selection.customer.phone || '';
  return (
    <div data-testid="takeaway-deposit-applied" className="grid gap-1 rounded-xl border border-blue-500/40 bg-blue-500/8 px-3 py-2">
      <div className="flex items-center gap-2">
        <HandCoins className="h-4 w-4 shrink-0 text-blue-700 dark:text-blue-300" />
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold tabular-nums text-t1" data-testid="takeaway-deposit-amount">
            {dt('deposits:takeaway.line', { amount: money(appliedCents / 100) })}
          </span>
          {who && <span className="block truncate text-[12px] text-t3">{who}</span>}
        </span>
        <button type="button" onClick={onOpen} disabled={disabled} data-testid="takeaway-deposit-change"
          className="h-9 shrink-0 rounded-lg border border-line bg-surface-1 px-3 text-[13px] font-medium text-t1 hover:bg-surface-2 disabled:opacity-40">
          {dt('deposits:takeaway.change')}
        </button>
        <button type="button" onClick={onRemove} disabled={disabled} aria-label={dt('deposits:takeaway.remove')} data-testid="takeaway-deposit-remove"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-t3 hover:bg-surface-2 hover:text-red-600 disabled:opacity-40">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex items-baseline justify-between gap-2 border-t border-blue-500/20 pt-1">
        <span className="text-[13px] text-t2">{dueCents === 0 ? dt('deposits:takeaway.coversAll') : dt('deposits:takeaway.toPay')}</span>
        <span className="text-[16px] font-bold tabular-nums text-t1" data-testid="takeaway-deposit-due">{dt('deposits:takeaway.due', { amount: money(dueCents / 100) })}</span>
      </div>
    </div>
  );
}
